import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {map, shareReplay, switchMap, take, tap} from 'rxjs/operators';

import {TerraUtils} from '../../../../shared/TerraUtils';
import {LpFundReqRes} from '../../../funds/LpFundReqRes.model';
import {FundReportReqRes} from '../../../../shared/models/FundReportReqRes.model';
import {FundReportDataService} from '../../../../services/shared/fund-report-data.service';
import {UtilsService} from '../../../../services/utils.service';
import {AppSettingsService} from '../../../../services/app-settings.service';
import {AnalyticsServiceNameModel, TelemetryService} from 'telemetry-library';
import {HoldingReportListItemReqRes} from '../../../../shared/models/HoldingReportListItemReqRes.model';
import {AssetReportReqRes} from '../../../../shared/models/AssetReportReqRes.model';
import HoldingDiscriminator from '../../../../shared/enums/HoldingDiscriminator.enum';
import {LpAssetReqRes} from '../../../assets/LpAssetReqRes.model';
import {AssetReportDataService} from '../../../../services/shared/asset-report-data.service';
import {LpHoldingService} from '../../../shared/holdings/lp-holding.service';

@Component({
  selector: 'terra-holding-reports',
  templateUrl: './holding-reports.component.html',
  styleUrls: ['./holding-reports.component.scss']
})
export class HoldingReportsComponent implements OnInit, OnDestroy {
  EXAMPLE_ASSET_ID = TerraUtils.consts.EXAMPLE_ASSET_ID;

  @Input() holding: LpFundReqRes | LpAssetReqRes;
  @Input() isScrolledToReport: boolean;
  @Input() holdingDiscriminator: HoldingDiscriminator;

  // enums
  HoldingDiscriminator = HoldingDiscriminator;

  displayedReportId$ = new BehaviorSubject(0);
  reports$ = this.lpHoldingService.holdingDetails$.pipe(map(p => p.reports));
  reportId$ = this.lpHoldingService.reportId$;

  reportsBasic$ = combineLatest([this.reports$, this.reportId$])
    .pipe(
      tap(([reportListItems, reportId]) => {
        if (reportListItems && reportListItems.length > 0) {
          let reportIdToSelect = reportId;
          // if no reportId in the querystring, or if it's not in the list, select the first report:
          if (!reportIdToSelect || !reportListItems.find(r => r.id === reportIdToSelect)) {
            reportIdToSelect = reportListItems[0].id;
          }
          this.displayedReportId$.next(reportIdToSelect);
        }
      }),
      map(([reportList, _]) => reportList),
      shareReplay(1)
    );

  reportDetails$: Observable<FundReportReqRes | AssetReportReqRes> = combineLatest([this.lpHoldingService.holdingId$, this.displayedReportId$])
    .pipe(
      switchMap(([holdingId, reportId]) => {
        if (this.isScrolledToReport) {
          this.createTelemetryEvent();
          if (holdingId && reportId) {
            this.scrollToReports();
          }
        }
        if (holdingId === TerraUtils.consts.EXAMPLE_ASSET_ID) {
          return of(this.getExampleAssetReport());
        } else if (this.holdingDiscriminator === HoldingDiscriminator.Fund) {
          return this.fundReportDataService.getByIdForLp(holdingId, reportId);
        }

        return this.assetReportDataService.getByIdForLp(holdingId, reportId);
      }));


  constructor(
    private lpHoldingService: LpHoldingService,
    private fundReportDataService: FundReportDataService,
    private assetReportDataService: AssetReportDataService,
    private utilsService: UtilsService,
    private appSettingsService: AppSettingsService,
    private telemetryService: TelemetryService) {
  }

  ngOnInit() {
    document.addEventListener('scroll', this.onScroll);
  }

  ngOnDestroy() {
    document.removeEventListener('scroll', this.onScroll);
  }

  getReportName(reportListItem: HoldingReportListItemReqRes) {
    return this.utilsService.getReportPeriodText(reportListItem.periodStartDate, reportListItem.periodEndDate);
  }

  getExampleAssetReport(): AssetReportReqRes {
    const exampleAssetReport = new AssetReportReqRes();
    exampleAssetReport.populateAsDummy(this.appSettingsService);
    return exampleAssetReport;
  }

  onScroll = () => {
    if (document.getElementById('reportsSection') !== null) {
      const reportsTop = document.getElementById('reportsSection').getBoundingClientRect().top;
      if (window.scrollY >= reportsTop) {
        this.isScrolledToReport = true;
        this.createTelemetryEvent();
        document.removeEventListener('scroll', this.onScroll);
      }
    } else {
      document.removeEventListener('scroll', this.onScroll);
    }
  }

  createTelemetryEvent() {
    this.lpHoldingService.holdingId$.pipe(take(1)).subscribe(holdingId => {
      this.displayedReportId$.pipe(take(1)).subscribe(reportId => {
        this.telemetryService.create({
          eventID: '203',
          eventTitle: 'LP OPENED REPORT',
          reportID: reportId,
          holdingID: holdingId,
          additional: {
            isMobileDevice: TerraUtils.isMobileDevice()
          }
        }, AnalyticsServiceNameModel.Insights);
      });
    });
  }

  scrollToReports() {
    const divReports = document.getElementById('focusReports');
    if (divReports) {
      divReports.scrollIntoView();
    }
  }
}
